import { Middleware } from '@reduxjs/toolkit'
import gamificationService from '../../services/gamificationService'
import { addNotification } from '../slices/uiSlice'

// Gamification middleware - Redux action'larını dinleyerek otomatik rozet kontrolü yapar
export const gamificationMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action)
  const state = store.getState()
  const userId = (state as any).auth.user?.uid
  
  if (!userId) return result
  
  // Action type'larına göre gamification event'lerini tetikle
  switch (action.type) {
    // Çalışma seansı tamamlandığında
    case 'study/completeSession':
      handleStudySessionComplete(store, userId, action.payload)
      break
      
    // Sınav tamamlandığında
    case 'exam/completeExam':
      handleExamComplete(store, userId, action.payload)
      break
      
    // Pomodoro tamamlandığında
    case 'pomodoro/completeSession':
      handlePomodoroComplete(store, userId, action.payload)
      break
      

      
    // Not oluşturulduğunda
    case 'notes/addNote':
      handleNoteCreated(store, userId, action.payload)
      break
      
    // Özet oluşturulduğunda
    case 'summaries/addSummary':
      handleSummaryCreated(store, userId, action.payload)
      break
      
    // Hedef tamamlandığında
    case 'study/completeGoal':
      handleGoalComplete(store, userId, action.payload)
      break
      
    default:
      break
  }
  
  return result
}

// Çalışma seansı tamamlama handler'ı
const handleStudySessionComplete = async (store: any, userId: string, payload: any) => {
  try {
    const { duration } = payload
    
    // Çalışma seansını kaydet
    await gamificationService.trackStudySession(userId, duration)
    
    // XP hesapla ve ekle (dakika başına 10 XP)
    const xpGained = Math.floor(duration / 60) * 10
    await gamificationService.addXP(userId, xpGained)
    
    // Bildirim göster
    store.dispatch(addNotification({
      type: 'success',
      title: 'Çalışma Seansı Tamamlandı!',
      message: `${Math.floor(duration / 60)} dakika çalıştınız. +${xpGained} XP kazandınız!`,
      read: false
    }))
    
    // Rozet kontrolü yap
    await checkAndAwardBadges(store, userId)
    
  } catch (error) {
    console.error('Çalışma seansı gamification hatası:', error)
  }
}

// Sınav tamamlama handler'ı
const handleExamComplete = async (store: any, userId: string, payload: any) => {
  try {
    const { score, totalQuestions } = payload
    
    // Sınav tamamlamasını kaydet
    await gamificationService.trackExamCompletion(userId, score)
    
    // XP hesapla (puan başına 10 XP + bonus)
    let xpGained = Math.floor(score * 10)
    
    // Yüksek puan bonusu
    const percentage = totalQuestions ? (score / totalQuestions) * 100 : 0
    if (percentage >= 90) {
      xpGained += 100 // %90+ için 100 bonus XP
    } else if (percentage >= 80) {
      xpGained += 50  // %80+ için 50 bonus XP
    }
    
    await gamificationService.addXP(userId, xpGained)
    
    // Bildirim göster
    store.dispatch(addNotification({
      type: 'success',
      title: 'Sınav Tamamlandı!',
      message: `${score} puan aldınız (${Math.round(percentage)}%). +${xpGained} XP!`,
      read: false
    }))
    
    // Rozet kontrolü yap
    await checkAndAwardBadges(store, userId)
    
  } catch (error) {
    console.error('Sınav tamamlama gamification hatası:', error)
  }
}

// Pomodoro tamamlama handler'ı
const handlePomodoroComplete = async (store: any, userId: string, payload: any) => {
  try {
    const { duration, completed } = payload
    
    if (completed) {
      const xpGained = 50 // Tamamlanan pomodoro için 50 XP
      await gamificationService.addXP(userId, xpGained)
      
      store.dispatch(addNotification({
        type: 'success',
        title: 'Pomodoro Tamamlandı! 🍅',
        message: `${duration} dakikalık pomodoro seansını tamamladınız. +${xpGained} XP!`,
        read: false
      }))
      
      // Rozet kontrolü yap
      await checkAndAwardBadges(store, userId)
    }
    
  } catch (error) {
    console.error('Pomodoro gamification hatası:', error)
  }
}



// Not oluşturma handler'ı
const handleNoteCreated = async (store: any, userId: string, _payload: any) => {
  try {
    const xpGained = 15 // Not oluşturma için 15 XP
    await gamificationService.addXP(userId, xpGained)
    
    store.dispatch(addNotification({
      type: 'info',
      title: 'Not Oluşturuldu!',
      message: `Yeni not oluşturdunuz. +${xpGained} XP!`,
      read: false
    }))
    
    // Rozet kontrolü yap
    await checkAndAwardBadges(store, userId)
    
  } catch (error) {
    console.error('Not oluşturma gamification hatası:', error)
  }
}

// Özet oluşturma handler'ı
const handleSummaryCreated = async (store: any, userId: string, _payload: any) => {
  try {
    const xpGained = 25 // Özet oluşturma için 25 XP
    await gamificationService.addXP(userId, xpGained)
    
    store.dispatch(addNotification({
      type: 'info',
      title: 'Özet Oluşturuldu!',
      message: `Yeni özet oluşturdunuz. +${xpGained} XP!`,
      read: false
    }))
    
    // Rozet kontrolü yap
    await checkAndAwardBadges(store, userId)
    
  } catch (error) {
    console.error('Özet oluşturma gamification hatası:', error)
  }
}

// Hedef tamamlama handler'ı
const handleGoalComplete = async (store: any, userId: string, payload: any) => {
  try {
    const { goalType, goalValue } = payload
    
    const xpGained = goalValue * 20 // Hedef değeri başına 20 XP
    await gamificationService.addXP(userId, xpGained)
    
    store.dispatch(addNotification({
      type: 'success',
      title: 'Hedef Tamamlandı! 🎯',
      message: `${goalType} hedefinizi tamamladınız. +${xpGained} XP!`,
      read: false
    }))
    
    // Rozet kontrolü yap
    await checkAndAwardBadges(store, userId)
    
  } catch (error) {
    console.error('Hedef tamamlama gamification hatası:', error)
  }
}

// Rozet kontrolü ve ödüllendirme
const checkAndAwardBadges = async (store: any, userId: string) => {
  try {
    // Kullanıcının mevcut progress'ini al
    const userProgress = await gamificationService.getUserProgress(userId)
    if (!userProgress) return
    
    // Tüm rozetleri al
    const allBadges = await gamificationService.getAllBadges()
    
    // Kullanıcının sahip olduğu rozetleri al
    const userBadges = await gamificationService.getUserBadges(userId)
    const userBadgeIds = userBadges.map(ub => ub.badgeId)
    
    // Henüz kazanılmamış rozetleri kontrol et
    for (const badge of allBadges) {
      if (userBadgeIds.includes(badge.id)) continue // Zaten kazanılmış
      
      // Rozet koşullarını kontrol et
      const isEligible = checkBadgeEligibility(badge, userProgress)
      
      if (isEligible) {
        // Rozeti kullanıcıya ver
        await gamificationService.unlockBadge(userId, badge.id)
        
        // Bildirim göster
        store.dispatch(addNotification({
          type: 'success',
          title: 'Yeni Rozet Kazandınız! 🏆',
          message: `"${badge.name}" rozetini kazandınız!`,
          read: false
        }))
        
        // Rozet XP'si kaldırıldı (Badge.xpReward yok)
      }
    }
    
  } catch (error) {
    console.error('Rozet kontrolü hatası:', error)
  }
}

// Rozet uygunluk kontrolü
const checkBadgeEligibility = (badge: any, userProgress: any): boolean => {
  if (!badge.conditions || badge.conditions.length === 0) return false
  
  // Tüm koşulları kontrol et
  return badge.conditions.every((condition: any) => {
    switch (condition.type) {
      case 'study_time':
        return userProgress.totalStudyTime >= condition.target
      case 'exams_completed':
        return userProgress.totalExamsCompleted >= condition.target
      case 'current_streak':
        return userProgress.currentStreak >= condition.target
      case 'longest_streak':
        return userProgress.longestStreak >= condition.target
      case 'level':
        return userProgress.level >= condition.target
      case 'xp':
        return userProgress.xp >= condition.target

      case 'notes_created':
        return (userProgress.notesCreated || 0) >= condition.target
      case 'summaries_created':
        return (userProgress.summariesCreated || 0) >= condition.target
      case 'pomodoros_completed':
        return (userProgress.pomodorosCompleted || 0) >= condition.target
      case 'perfect_exams':
        return (userProgress.perfectExams || 0) >= condition.target
      case 'consecutive_days':
        return userProgress.currentStreak >= condition.target
      default:
        return false
    }
  })
}

export default gamificationMiddleware